import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useParams, useQuery } from '../../../contexts/RouterContext';
import ProjectGroupsModule from '../../../redux/project_grouping';
import { nuc } from '../../../utils';
import {
  DEFAULT_REMEDIATION_FORMAT,
  isReleaseGroupScope,
  type ReportsV3Params,
  type ReportsV3QueryParams,
} from '../types';

import { useRemediationOptionsParams } from './useRemediationOptionsParams';

/**
 * Builds the preview URL for the remediation guidance report.
 * Same pattern as `usePreviewURL` but targets the remediation-guidance endpoints
 * instead of the attribution endpoints, and does not use the REPORTS_V2 feature flag.
 */
export const useRemediationPreviewURL = (): string => {
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);
  const [query] = useQuery<ReportsV3QueryParams>();
  const reportFormat = query.format ?? DEFAULT_REMEDIATION_FORMAT;
  const selectedReleaseId = useSelector(ProjectGroupsModule.selectors.getSelectedReleaseId);
  const optionsParams = useRemediationOptionsParams();

  return useMemo(() => {
    const optionsSuffix = optionsParams ? `&${optionsParams}` : '';

    if (isReleaseGroup) {
      const { projectGroupId, releaseId } = params;
      const resolvedReleaseId = releaseId ?? selectedReleaseId ?? 0;
      return `/api/project_group/${projectGroupId}/release/${resolvedReleaseId}/report/remediation-guidance?preview=true&format=${reportFormat}${optionsSuffix}`;
    }

    const projectLocator = nuc(`${params.project_id}$${params.revision_id}`);
    return `/api/revisions/${projectLocator}/report/remediation-guidance?preview=true&format=${reportFormat}${optionsSuffix}`;
  }, [isReleaseGroup, optionsParams, params, reportFormat, selectedReleaseId]);
};
